import { useEffect,useState } from 'react';
import { useNavigate } from 'react-router-dom';

import fetchWrapper from "@/services/fetchWrapper.service";

import useStateHandler from './useStateHandler';

const inflightRequests = new Map();

/**
 * Custom hook used for everything related to getting the data for your components in an efficient way. It uses my own cache system for efficient fetching.
 * @param {string} cacheKey - The key used to store the cached data
 * @param {string} url - The API endpoint to fetch data from
 * @param {Object} options - An optional object containing options to customize the behavior of the hook.
 * @returns {Object} An object that contains the loading state, the requested data and a function to manually trigger a request
 */
export default function useCache(cacheKey, url, options={}) {
  const { cacheHandler, state } = useStateHandler();
  const isDisabled = options.disabled === true;
  const initialData =
    cacheHandler.getCacheValue(cacheKey)?.data
    ?? options.initialData
    ?? null;
  const [fetchData, _setFetchData] = useState(initialData); /** Cached values are stored in render phase to increase speed */
  const [loading, setLoading] = useState(
    isDisabled
      ? false
      : (Boolean(fetchData) && (cacheHandler.getCacheValue(cacheKey)?.isDataInvalid === false || Boolean(options.initialData))) ? false : true
  );
  const dependencyArray = options.invalidateWhen ? options.invalidateWhen.map(action => state.triggeredActions[action]) : []; /** This will configure the useEffect to execute again only when one action that invalidates the data triggers */
  dependencyArray.push(isDisabled);

  const navigate = useNavigate();

  function _buildRequestUrl() {
    return `${url}${_getQueryString()}`;
  }

  function _getInflightRequestKey() {
    return JSON.stringify({
      url: _buildRequestUrl(),
      injectToken: options.injectToken === true,
      optionalToken: options.optionalToken === true,
    });
  }

  function _getQueryString() {
    let parts = [];

    for(const key in options.includeSearchQueries) {
      const value = options.includeSearchQueries[key];

      if(value) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }

    return '?' + parts.join('&');
  }

  async function _fetchNewData() {
    if (isDisabled) {
      return;
    }
    setLoading(true);

    const requestKey = _getInflightRequestKey();
    let requestPromise = inflightRequests.get(requestKey);

    if (!requestPromise) {
      requestPromise = fetchWrapper.get(_buildRequestUrl(), {
        injectToken: options.injectToken,
        optionalToken: options.optionalToken,
      });
      inflightRequests.set(requestKey, requestPromise);
    }

    let response;
    let data;

    try {
      ({ response, data } = await requestPromise);
    } finally {
      if (inflightRequests.get(requestKey) === requestPromise) {
        inflightRequests.delete(requestKey);
      }
    }

    if(response.ok) {
      setLoading(false);
      setFetchData(data);
    } else if([401, 403].includes(response.status)) {
      navigate("/", { replace: true });
    }
  }

  function refetch() { 
    return _fetchNewData();
  }

  async function loadMoreData() {
    if (isDisabled) {
      return;
    }
    const { response, data } = await fetchWrapper.get(`${url}${_getQueryString()}&offset=${fetchData.data.length}`, {
      injectToken: options.injectToken,
      optionalToken: options.optionalToken,
    });

    if(response.ok) {
      setFetchData({ ...data, data: [...fetchData.data, ...data.data] });
    } else if([401, 403].includes(response.status)) {
      navigate("/", { replace: true });
    }
  }

  function setFetchData(data) {
    let dataToStore = data;

    _setFetchData(prevValue => {
      if(typeof data === "function") {
        dataToStore = data(prevValue);
      }

      return dataToStore;
    });

    if(typeof dataToStore !== "function") {
      cacheHandler.storeInCache(cacheKey, dataToStore, options); 
    }
  }

  useEffect(() => {
    if (isDisabled) {
      return;
    }
    const cacheValue = cacheHandler.getCacheValue(cacheKey);

    if (!cacheValue && options.initialData) {
      cacheHandler.storeInCache(cacheKey, options.initialData, options);
      return;
    }

    if(!cacheValue || cacheValue.isDataInvalid) {
      _fetchNewData();
    }
  }, dependencyArray);

  return { loading, fetchData, setFetchData, loadMoreData, refetch };
}
